import { DateTime } from 'luxon'
import { addUnits, startOfUnit } from './timeScale'
import type { TimelineEvent, TimelineRow, TimeUnit } from './types'

export interface ScheduleSection {
  key: string
  label: string
  level?: number | string
  open?: number | string | boolean
  children?: ScheduleSection[]
  [key: string]: any
}

export interface ScheduleItem {
  id: string | number
  start_date: string
  end_date: string
  section_id?: string
  [key: string]: any
}

export interface ScheduleResponse {
  data?: ScheduleItem[]
  collections?: { sections?: ScheduleSection[] }
}

const SCHEDULE_DATE = /^(\d{4})-(\d{2})-(\d{2}) (\d{1,2}):(\d{2})$/

export function parseScheduleDate (value: string | null | undefined): Date | null {
  if (!value) {
    return null
  }
  const match = SCHEDULE_DATE.exec(String(value))
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]))
  }
  const parsed = DateTime.fromFormat(String(value), 'yyyy-MM-dd HH:mm')
  if (parsed.isValid) {
    return parsed.toJSDate()
  }
  const fallback = DateTime.fromSQL(String(value))
  return fallback.isValid ? fallback.toJSDate() : null
}

export function formatLoadDate (date: Date): string {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
}

export function toRows (sections: ScheduleSection[] | undefined): TimelineRow[] {
  const map = (section: ScheduleSection): TimelineRow => {
    const { key, label, level, open, children, ...meta } = section
    return {
      key: String(key),
      label: String(label ?? ''),
      level: Number(level) || 0,
      open: open === true || Number(open) === 1,
      children: children && children.length ? children.map(map) : undefined,
      meta
    }
  }
  return (sections || []).map(map)
}

export function toEvent (item: ScheduleItem): TimelineEvent | null {
  const start = parseScheduleDate(item.start_date)
  const end = parseScheduleDate(item.end_date)
  if (!start || !end) {
    return null
  }
  return {
    id: String(item.id),
    start,
    end: end.getTime() > start.getTime() ? end : new Date(start.getTime() + 60000),
    rowKeys: item.section_id ? String(item.section_id).split(',').filter(Boolean) : [],
    className: item.class ? String(item.class) : undefined,
    data: item
  }
}

export interface LoaderState {
  rows: TimelineRow[]
  events: TimelineEvent[]
  loading: boolean
}

export interface RangeLoaderOptions {
  unit: TimeUnit
  fetchChunk: (from: Date, to: Date, signal: AbortSignal) => Promise<ScheduleResponse>
  decorate?: (event: TimelineEvent) => TimelineEvent | null
  onUpdate: (state: LoaderState) => void
  onError?: (error: unknown) => void
}

interface Chunk {
  start: Date
  end: Date
  version: number
  events: Map<string, TimelineEvent> | null
  controller: AbortController | null
}

// Daily views load one month per request and hourly views one week, starting on Monday
export function createRangeLoader (options: RangeLoaderOptions) {
  const chunks = new Map<number, Chunk>()
  let rows: TimelineRow[] = []
  let pending = 0
  let lastRange: { start: Date, end: Date } | null = null
  let disposed = false

  const chunkStart = (date: Date): Date => {
    const d = startOfUnit(date, 'day')
    if (options.unit === 'hour') {
      d.setDate(d.getDate() - (d.getDay() + 6) % 7)
      return d
    }
    d.setDate(1)
    return d
  }

  const nextChunk = (date: Date): Date => {
    if (options.unit === 'hour') {
      return addUnits(date, 'day', 7)
    }
    const d = new Date(date.getTime())
    d.setMonth(d.getMonth() + 1)
    return d
  }

  const chunksFor = (start: Date, end: Date): Date[] => {
    const list: Date[] = []
    for (let d = chunkStart(start); d.getTime() < end.getTime(); d = nextChunk(d)) {
      list.push(d)
    }
    return list
  }

  const publish = () => {
    if (disposed) {
      return
    }
    const merged = new Map<string, TimelineEvent>()
    const keys = [...chunks.keys()].sort((a, b) => a - b)
    for (const key of keys) {
      const chunk = chunks.get(key) as Chunk
      if (chunk.events) {
        for (const [id, event] of chunk.events) {
          merged.set(id, event)
        }
      }
    }
    options.onUpdate({ rows, events: [...merged.values()], loading: pending > 0 })
  }

  const fetch = async (chunk: Chunk) => {
    const version = ++chunk.version
    if (chunk.controller) {
      chunk.controller.abort()
    }
    const controller = new AbortController()
    chunk.controller = controller
    pending++
    publish()
    try {
      const response = await options.fetchChunk(chunk.start, chunk.end, controller.signal)
      if (disposed || version !== chunk.version) {
        return
      }
      if (response && response.collections && response.collections.sections) {
        rows = toRows(response.collections.sections)
      }
      const events = new Map<string, TimelineEvent>()
      for (const item of (response && response.data) || []) {
        let event = toEvent(item)
        if (event && options.decorate) {
          event = options.decorate(event)
        }
        if (event) {
          events.set(event.id, event)
        }
      }
      chunk.events = events
    } catch (e) {
      if (options.onError && !controller.signal.aborted) {
        options.onError(e)
      }
    } finally {
      if (chunk.controller === controller) {
        chunk.controller = null
      }
      pending--
      publish()
    }
  }

  const abort = (chunk: Chunk) => {
    if (chunk.controller) {
      chunk.controller.abort()
      chunk.controller = null
    }
  }

  const ensure = (start: Date, end: Date) => {
    lastRange = { start, end }
    for (const date of chunksFor(start, end)) {
      const key = date.getTime()
      if (!chunks.has(key)) {
        const chunk: Chunk = { start: date, end: nextChunk(date), version: 0, events: null, controller: null }
        chunks.set(key, chunk)
        fetch(chunk)
      }
    }
  }

  // Drops chunks outside the current range and refetches the rest
  const reload = () => {
    if (!lastRange) {
      return
    }
    const wanted = new Set(chunksFor(lastRange.start, lastRange.end).map(d => d.getTime()))
    for (const [key, chunk] of [...chunks.entries()]) {
      if (!wanted.has(key)) {
        abort(chunk)
        chunks.delete(key)
      }
    }
    for (const key of wanted) {
      let chunk = chunks.get(key)
      if (!chunk) {
        const date = new Date(key)
        chunk = { start: date, end: nextChunk(date), version: 0, events: null, controller: null }
        chunks.set(key, chunk)
      }
      fetch(chunk)
    }
  }

  const hasEvent = (id: string | number) => {
    const key = String(id)
    for (const chunk of chunks.values()) {
      if (chunk.events && chunk.events.has(key)) {
        return true
      }
    }
    return false
  }

  const dispose = () => {
    disposed = true
    for (const chunk of chunks.values()) {
      abort(chunk)
    }
    chunks.clear()
  }

  return { ensure, reload, hasEvent, dispose }
}
